/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import { getVersion } from "../utils/version";

const version = getVersion();

const Config = {
    version: version,
    features: {
        Dashboard: {
            Enabled: true,
            hideAsComingSoon: true
        },
        Tasks: {
            Enabled: true,
            hideAsComingSoon: true
        },
        Composer: {
            Enabled: true
        },
        Websites: {
            Enabled: true,
            hideAsComingSoon: false,
            Publishing: {
                Enabled: true,
                Alerts: {
                    // Enabled: process.env.REACT_APP_PUBLISHING_DEBUG === 'true' || false,
                    Enabled: false,
                },
                History: {
                    // Enabled: process.env.REACT_APP_PUBLISHING_DEBUG === 'true' || false,
                    Enabled: false,
                },
            }
        },
        Forms: {
            Enabled: true,
            hideAsComingSoon: false
        },
        Digital_Assets: {
            Enabled: true,
            hideAsComingSoon: false
        },
        Models: {
            Enabled: true,
            hideAsComingSoon: false
        },
        Data: {
            Enabled: false,
            hideAsComingSoon: true
        },
        Components: {
            Enabled: false,
            hideAsComingSoon: true
        },
        OrganizationsSettings: {
            Enabled: false,
            hideAsComingSoon: true,
            Users: {
                Enabled: false
            },
            Roles: {
                Enabled: false
            },
        },
        Testing: {
            Enabled: false
        },
        Notifications: {
            Enabled: false
        }
    },
    webpage: {
        enableShortlinks: false
    },
    enableSchemaDefaults: false,
    Debug: {
        useDebug: process.env.REACT_APP_SHOW_DEBUG === "true" || false,
        inDevelopment: process.env.REACT_APP_ENVIRONMENT === "development" || false,
        enableDiagnostics: process.env.REACT_APP_ENABLE_DIAGNOSTICS === "true" || false,
        useRichTextDebug: process.env.REACT_APP_RICHTEXT_DEBUG === "true" || false,
        // enableLinkDebug: process.env.REACT_APP_LINK_DEBUG === 'true' || false,
        enableComponentDebug: process.env.REACT_APP_COMPONENT_DEBUG === "true" || false,
        enableFieldDebug: process.env.REACT_APP_FIELD_DEBUG === "true" || false,
    },
    Logging: {
        SourceToken: process.env.REACT_APP_LOG_SOURCE_TOKEN,
        enableConsole: process.env.REACT_APP_ENABLE_CONSOLE_LOG === "true" || false,
        disableCloud: process.env.REACT_APP_DISABLE_CLOUD_LOG === "true" || false,
        enableApiHttp: process.env.REACT_APP_ENABLE_HTTP_LOG === "true" || false,
        enableSavePipelineLog: process.env.REACT_APP_ENABLE_SAVE_PIPELINE_LOG === "true" || false,
        enableWebpageLog: process.env.REACT_APP_ENABLE_WEBPAGE_LOG === "true" || false,
        rebuildStack: process.env.REACT_APP_REBUILDSTACK === "true" || false,
    },
    drawerWidth: 260,
    throttleWait: 2000,
    eventDebounce: 400,
    dates: {
        timezone: "America/Vancouver",
        general_long: "MMM D, YYYY, h:mma", // only for moment
        detailed: "MMM D, YY, h:mma",
        general_med: "ddd, D MMM, YYYY", // only for moment
        general_short: "MMM D, YYYY",
        // format to write to storage,
        // ...also parse to read from storage
        internalFormats: {
            datetime: "YYYY-MM-DDTHH:mm:ssZZ", // ... but just use .toISOString() to format as a datetime
            date: "YYYY-MM-DD",
            time: "hh:mm A", // 12 hour 2 digit, space, MUST use uppercase AM/PM: 07:15 PM
        },
        // format pattern used to format for display
        display: {
            date: "MMMM DD, YYYY", // January 05, 2025
            time: "h:mma", // 12 hour 1 digit: 7:15pm
            datetime: "MMMM DD, YYYY h:mma", // 12 hour 1 digit, lowercase am/pm: 7:15pm
            datetime_short: "MMM DD, YYYY h:mma", // 12 hour 1 digit, lowercase am/pm: 7:15pm
        },
    },
    /**
     * Set of date-fns overrides that work specifically for react-datepicker
     */
    dateFnDates: {
        edit: {
            date: "MMMM dd, yyyy",
            time: "hh:mm a",
            datetime: "MMMM dd, yyyy hh:mm a",
        },
    },
    forms: {
        files: {
            defaultMaxSize: 40 * 1024 * 1024,
            defaultMaxSizeDisplay: "40mb",
        }
    },
    defaultPath: "/dashboard/default",
    fontFamily: "'Public Sans', sans-serif",
    i18n: "en",
    miniDrawer: false,
    container: true,
    mode: "light",
    presetColor: "default",
    themeDirection: "ltr",
    consoleUrl: process.env.REACT_APP_SIMPLICITY_CONSOLE_URL,
    imageBase: process.env.REACT_APP_SIMPLICITY_SDK_IMAGE_URL,
    documentBase: process.env.REACT_APP_SIMPLICITY_SDK_DOCUMENT_URL,
    previewUrl: process.env.REACT_APP_SIMPLICITY_SDK_PREVIEW_URL,
    siteUrl: process.env.REACT_APP_SIMPLICITY_SDK_PREVIEW_URL,
    COOKIE_DOMAIN: process.env.REACT_APP_SIMPLICITY_COOKIE_DOMAIN,
    drawerCookie: "simplicity_drawer_open",
    API_BASEURL: process.env.REACT_APP_SIMPLICITY_SDK_API_BASE_URL,
    SIMPLICITY_AUTH_REDIRECT: process.env.REACT_APP_SIMPLICITY_AUTH_REDIRECT,
    API_PREFIX: "/api",
    JWT_API: {
        secret: "asupersecretkey",
        timeout: "1 days",
    },
    LOGIN_URL: process.env.REACT_APP_LOGIN_URL,
    LOGOUT_URL: process.env.REACT_APP_LOGOUT_URL,
    Links: {
        Integration: "https://app.dnv.org",
    },
    Azure: {
        AZURE_REDIRECT_URI: process.env.REACT_APP_SIMPLICITY_CONSOLE_URL,
        msalConfig: {
            auth: {
                clientId: process.env.REACT_APP_AZURE_CLIENT_ID,
                authority: `https://login.microsoftonline.com/${process.env.REACT_APP_AZURE_TENANT_ID}`,
                redirectUri:  process.env.REACT_APP_SIMPLICITY_CONSOLE_URL,
            },
            cache: {
                cacheLocation: "localStorage", // This configures where your cache will be stored
                storeAuthStateInCookie: true, // Set this to "true" if you are having issues on IE11 or Edge
            }
        },
    },
    FontAwesome: {
        Version: "6.5.1",
        API_Token: process.env.REACT_APP_FONT_AWESOME_API_TOKEN,
    },
    Images: {
        // https://www.iana.org/assignments/media-types/media-types.xhtml#image
        accept: {
            "image/svg+xml": [],
            "image/png": [],
            "image/jpeg": [],
            "image/webp": [],
            "image/heic": [],
            "image/heif": [],
            // 'image/tiff': [],
            // 'image/vnd.adobe.photoshop': [],
            // 'image/*': [],               // all
        }
    },
    Documents: {
        accept: {
            "application/pdf": [],
            // '.doc': [],
            // '.docx': [],
            "application/msword": [],
            "application/vnd.ms-excel": [],
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [],
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [],
            "application/vnd.openxmlformats-officedocument.presentationml.presentation": [],
        }
    },
    Video: {
        // https://www.iana.org/assignments/media-types/media-types.xhtml#video
        accept: {
            "video/mp4": [],
            "video/quicktime": [],
            "video/webm": [],
            "video/ogg": [],
            "video/AV1": [],
            // 'video/x-flv': [],
            // 'video/MP2T': [],
            // 'video/3gpp': [],
            // 'application/x-mpegURL': [],
            // 'video/x-msvideo': [],
            // 'video/x-ms-wmv': [],
            // 'video/*': [],               // all
        }
    },
    Other: {
        // https://www.iana.org/assignments/media-types/media-types.xhtml#text
        // https://www.iana.org/assignments/media-types/media-types.xhtml#application
        // https://www.iana.org/assignments/media-types/media-types.xhtml#audio
        accept: {
            "application/json": [],
            "text/xml": [],
            "text/plain": [],
            "text/calendar": [],
            "application/vnd.android.package-archive": [],
            "text/csv": [],
            "application/x-zip-compressed": [],
        }
    }
};
// https://learn.microsoft.com/en-us/azure/active-directory/develop/v2-protocols-oidc#send-a-sign-out-request

export { Config };
export default Config;
